import { useState } from "react";
import { ArrowUpRight, ImageOff } from "lucide-react";
import { StatusDot } from "@shared/components/blocks";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import type { MarketingFileView, MarketingItemView } from "@shared/lib/marketing-shared";
import { cn } from "@shared/lib/utils";
import { DownloadAction, ViewAction } from "./file-actions";
import { PostText } from "./post-text";
import { PreviewDialog } from "./preview-dialog";

/** Titre, statut (avec le lien vers la publication) et ligne méta d'un contenu. */
function AssetHead({ item }: { item: MarketingItemView }) {
  return (
    <div className="min-w-0">
      <h3 className="m-0 text-[14px] leading-snug font-semibold">{item.title}</h3>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
        <StatusDot tone={item.statusTone}>{item.statusLabel}</StatusDot>
        {item.publications
          .filter((publication) => publication.url)
          .map((publication) => (
            <a
              className="inline-flex items-center gap-1 text-[12.5px] font-medium text-muted-foreground transition-colors hover:text-foreground"
              href={publication.url!}
              key={`${publication.channelLabel}-${publication.url}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {publication.linkLabel}
              <ArrowUpRight aria-hidden="true" size={13} />
            </a>
          ))}
      </div>
      <p className="m-0 mt-1 text-[12.5px] text-subtle-foreground">
        {[item.summary, item.updatedLabel].filter(Boolean).join(" · ")}
      </p>
    </div>
  );
}

/** Description et usage conseillé. */
function AssetNotes({ item, className }: { item: MarketingItemView; className?: string }) {
  if (!item.description && !item.usage) return null;
  return (
    <div className={cn("min-w-0 max-w-[78ch] text-[13px] leading-relaxed", className)}>
      {item.description && <p className="m-0">{item.description}</p>}
      {item.usage && (
        <p className="m-0 mt-1 text-muted-foreground">
          <span className="font-medium text-foreground">Usage conseillé :</span> {item.usage}
        </p>
      )}
    </div>
  );
}

/** En-tête complet d'un contenu, empilé (groupe de logos). */
export function AssetHeading({ item }: { item: MarketingItemView }) {
  return (
    <div className="min-w-0">
      <AssetHead item={item} />
      <AssetNotes className="mt-2" item={item} />
    </div>
  );
}

function Thumbnail({ item, onPreview }: { item: MarketingItemView; onPreview: (key: string) => void }) {
  const box = "block w-full overflow-hidden rounded-md border";
  if (!item.thumbnail) {
    return (
      <div className={cn(box, "flex aspect-[4/3] flex-col items-center justify-center gap-1 bg-muted p-2 text-center")}>
        <ImageOff aria-hidden="true" className="size-4 text-muted-foreground" />
        <span className="text-[12px] text-muted-foreground">Vignette absente</span>
      </div>
    );
  }
  const image = (
    <img
      alt=""
      className={cn("size-full object-cover", item.thumbnail.background === "dark" ? "bg-foreground" : "bg-muted")}
      loading="lazy"
      src={item.thumbnail.url}
    />
  );
  const style = { aspectRatio: item.thumbnail.aspect };

  // Un document s'ouvre dans un nouvel onglet ; le reste dans l'aperçu.
  const pdf = item.variants.find((file) => file.kind === "pdf" && !file.missing);
  if (item.type === "document" && pdf) {
    return (
      <a aria-label={`Ouvrir ${item.title}`} className={cn(box, "hover:border-border-strong")} href={pdf.url} rel="noopener noreferrer" style={style} target="_blank">
        {image}
      </a>
    );
  }
  const target =
    item.type === "carousel" && item.slides.some((slide) => !slide.missing)
      ? "slide-1"
      : item.variants.find((file) => (file.kind === "video" || file.kind === "image") && !file.missing)?.key;
  if (!target) {
    return (
      <div className={box} style={style}>
        {image}
      </div>
    );
  }
  return (
    <button
      aria-label={`Aperçu : ${item.title}`}
      className={cn(box, "cursor-pointer p-0 hover:border-border-strong")}
      onClick={() => onPreview(target)}
      style={style}
      type="button"
    >
      {image}
    </button>
  );
}

function FileRow({ file, onPreview }: { file: MarketingFileView; onPreview: (key: string) => void }) {
  const facts = [file.format, file.detail].filter(Boolean).join(" · ");
  const dimensions = [file.dimensions, file.ratio].filter(Boolean).join(" · ");
  return (
    <TableRow className={cn(file.missing && "text-muted-foreground hover:bg-transparent")}>
      <TableCell className="py-2 pl-0 whitespace-normal">
        <span className="block text-[13px] font-medium">{file.label}</span>
        {file.usage && <span className="block text-[12px] text-muted-foreground">{file.usage}</span>}
        {/* Mobile : format, dimensions et poids repliés sous le libellé. */}
        {!file.missing && (
          <span className="block text-[12px] text-muted-foreground sm:hidden">
            {[file.format, file.dimensions, file.size].filter(Boolean).join(" · ")}
          </span>
        )}
        {file.missing && <StatusDot className="mt-0.5" tone="danger">Fichier absent</StatusDot>}
      </TableCell>
      {/* Entre sm et lg, les colonnes sont étroites : le texte passe à la ligne plutôt que de finir en ellipse. */}
      <TableCell className="text-[13px] whitespace-normal text-muted-foreground max-sm:hidden">{facts}</TableCell>
      <TableCell className="text-[13px] whitespace-normal text-muted-foreground tabular-nums max-sm:hidden">{dimensions || "—"}</TableCell>
      <TableCell className="text-right text-[13px] whitespace-nowrap tabular-nums max-sm:hidden">{file.size ?? "—"}</TableCell>
      <TableCell className="py-1.5 pr-0">
        <div className="flex justify-end gap-0.5">
          <ViewAction file={file} onPreview={onPreview} />
          <DownloadAction file={file} />
        </div>
      </TableCell>
    </TableRow>
  );
}

/** Un contenu (vidéo, carrousel, visuel, document) : vignette, fiche, fichiers, texte du post. */
export function AssetRow({ item }: { item: MarketingItemView }) {
  const [preview, setPreview] = useState<string | null>(null);
  return (
    <article className="px-4 py-4 max-sm:px-3.5">
      {/* Mobile : la description passe sous la vignette, sur toute la largeur. */}
      <div className="grid grid-cols-[120px_minmax(0,1fr)] grid-rows-[auto_1fr] items-start gap-x-4 gap-y-2 max-sm:grid-cols-[88px_minmax(0,1fr)] max-sm:gap-x-3 max-sm:gap-y-3">
        <div className="sm:row-span-2">
          <Thumbnail item={item} onPreview={setPreview} />
        </div>
        <AssetHead item={item} />
        <AssetNotes className="max-sm:col-span-2" item={item} />
      </div>
      {/* Largeurs fixes : les colonnes s'alignent d'un contenu à l'autre. */}
      <Table className="mt-3 table-fixed">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="pl-0">Fichier</TableHead>
            <TableHead className="w-[24%] max-sm:hidden">Format</TableHead>
            <TableHead className="w-[20%] max-sm:hidden">Dimensions</TableHead>
            <TableHead className="w-24 text-right max-sm:hidden">Poids</TableHead>
            <TableHead className="w-[76px] pr-0 max-sm:w-[92px]">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {item.variants.map((file) => (
            <FileRow file={file} key={file.key} onPreview={setPreview} />
          ))}
        </TableBody>
      </Table>
      <PostText post={item.post} texts={item.texts} />
      <PreviewDialog item={item} onClose={() => setPreview(null)} selected={preview} />
    </article>
  );
}
